import React, { useState } from 'react';
import { Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }

    const token = searchParams.get('token');
    if (!token) { setError('Invalid reset link.'); return; }

    setError(null);
    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, password });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: 'calc(100vh - 260px)', display: 'grid', placeItems: 'center', px: 2, py: { xs: 5, md: 7 }, bgcolor: '#f5f7fb' }}>
      <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, boxShadow: '0 20px 50px rgba(7,24,45,0.08)', width: '100%', maxWidth: 480, borderColor: 'rgba(7,24,45,0.08)' }}>
        {success ? (
          <Stack spacing={2} alignItems="center">
            <Typography variant="h5" fontWeight={700}>Password reset!</Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Your password has been updated. You can now sign in.
            </Typography>
            <Button component={RouterLink} to="/login" variant="contained" color="secondary">
              Go to login
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2} component="form" onSubmit={handleSubmit}>
            <Typography variant="h5" fontWeight={700}>Set a new password</Typography>
            <Typography variant="body2" color="text.secondary">
              Choose a strong password with at least 6 characters.
            </Typography>
            <TextField
              label="New password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="Confirm password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              fullWidth
            />
            {error && <Typography variant="body2" color="error">{error}</Typography>}
            <Button type="submit" variant="contained" color="secondary" size="large" disabled={loading}>
              {loading ? 'Resetting…' : 'Reset password'}
            </Button>
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default ResetPassword;
